import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { format } from "date-fns";
import { PrinterFill, Boxes } from "react-bootstrap-icons";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";

const createLoadReferenceWorker = createWorkerFactory(
  () => import("../workers/load-references")
);

export interface LocationItem {
  location: string;
  rackPosition: string;
  writeTimestamp: string;
  warehouse: string;
  inboundLoadReference: string;
  id: string;
  createdTimestamp: string;
}

export const InboundLoadTracker: React.FC = () => {
  const [loadReference, setLoadReference] = useState<string>("");
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadReferenceWorker = useWorker(createLoadReferenceWorker);

  const fetchLocations = async () => {
    if (!loadReference.trim()) {
      setErrorMessage("Please enter an inbound load reference");
      return;
    }

    setIsLoading(true);
    setErrorMessage(undefined);
    setSuccessMessage(null);

    try {
      const storedItemsByLoadReference =
        await loadReferenceWorker.getByLoadReference(loadReference);
      setLocations(storedItemsByLoadReference);
      if (
        !storedItemsByLoadReference ||
        storedItemsByLoadReference.length === 0
      ) {
        setErrorMessage("No items found with this load reference");
      } else {
        setSuccessMessage(
          `Found ${storedItemsByLoadReference.length} item(s) with load reference: ${loadReference}`
        );
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? `Failed to fetch locations: ${error.message}`
          : "Failed to fetch locations"
      );
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllInboundItems = async () => {
    setIsLoading(true);
    setErrorMessage(undefined);
    setSuccessMessage(null);
    setLoadReference("");

    try {
      const allInboundItems = await loadReferenceWorker.getInboundStoredItems();
      setLocations(allInboundItems);
      if (!allInboundItems || allInboundItems.length === 0) {
        setErrorMessage("No inbound storage items found");
      } else {
        setSuccessMessage(
          `Found ${allInboundItems.length} total inbound storage items`
        );
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? `Failed to fetch all inbound items: ${error.message}`
          : "Failed to fetch all inbound items"
      );
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm:ss");
    } catch (error) {
      return dateString;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLocations();
  };

  const handlePrint = () => {
    window.print();
  };

  const currentDate = new Date();
  const formattedCurrentDate = format(currentDate, "dd/MM/yyyy HH:mm:ss");

  return (
    <div>
      {/* Regular view - hidden during print */}
      <div className="no-print">
        <Container>
          <Card className="shadow-sm">
            <Card.Header as="h4" className="">
              Inbound Storage Tracker
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row className="align-items-end mb-4">
                  <Col md={7}>
                    <Form.Group controlId="loadReference">
                      <Form.Control
                        type="text"
                        value={loadReference}
                        onChange={(e) => setLoadReference(e.target.value)}
                        placeholder="Enter load reference (e.g., OAK27032025)"
                        autoComplete="off"
                        autoFocus
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={isLoading}
                    >
                      {isLoading && loadReference ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Searching...
                        </>
                      ) : (
                        "Search"
                      )}
                    </Button>
                  </Col>
                  <Col md={3} className="mt-md-0 mt-2">
                    <Button
                      variant="success"
                      onClick={fetchAllInboundItems}
                      className="w-100"
                      disabled={isLoading}
                    >
                      {isLoading && !loadReference ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Boxes className="me-2" />
                          All Inbound Items
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>

              {errorMessage && (
                <Alert variant="danger" className="mt-3">
                  {errorMessage}
                </Alert>
              )}

              {successMessage && (
                <Alert variant="success" className="mt-3">
                  {successMessage}
                </Alert>
              )}

              {locations && locations.length > 0 && (
                <div className="mt-4">
                  <Row className="mb-3 align-items-center">
                    <Col>
                      <h5>Results</h5>
                    </Col>
                    <Col xs="auto">
                      <Button
                        variant="outline-secondary"
                        onClick={handlePrint}
                        className="d-flex align-items-center"
                      >
                        <PrinterFill className="me-2" />
                        Print Results
                      </Button>
                    </Col>
                  </Row>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Location</th>
                          <th>Rack Position</th>
                          <th>Load Reference</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locations.map((item) => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>{item.location}</td>
                            <td>{item.rackPosition}</td>
                            <td>{item.inboundLoadReference}</td>
                            <td>{formatDate(item.createdTimestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Print-only view - shown only during printing */}
      {locations && locations.length > 0 && (
        <div className="print-only">
          <div className="print-header">
            <h4>
              {loadReference
                ? `Inbound Load Reference: ${loadReference}`
                : "All Inbound Storage Items"}
            </h4>
            <p className="print-info">
              Report generated: {formattedCurrentDate}
            </p>
            <p className="print-info">Total items: {locations.length}</p>
          </div>

          <table className="print-only-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Location</th>
                <th>Rack Position</th>
                <th>Load Reference</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((item) => (
                <tr key={`print-${item.id}`}>
                  <td>{item.id}</td>
                  <td>{item.location}</td>
                  <td>{item.rackPosition}</td>
                  <td>{item.inboundLoadReference}</td>
                  <td>{formatDate(item.createdTimestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InboundLoadTracker;
